import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Space } from 'antd';
import { get, filter } from 'lodash';
import { searchParamList } from '../../../../helpers/AppHelpers';
import {
  getKeySkills,
  getSearchResultsByUsers,
  getPositions,
  getContractTypes,
} from '../../../../services/api';
import OptionFilter from './filters/OptionFilter';
import UserNameFilter from './filters/UserNameFilter';
import ProjectFilter from './filters/ProjectFilter';
import ContractTypeFilter from './filters/ContractTypeFilter';
import TechSkillsFilter from './filters/TechSkillsFilter';
import KeySkillsFilter from './filters/KeySkillsFilter';
import IsFireFilter from './filters/IsFireFilter';
import EnglishFilter from './filters/EnglishFilter';
import PositionFilter from './filters/PositionFilter';
import config from '../../../../config';
import { companyName } from '../../../../constants';

export default function SearchBar({ setFilteredUserData, setLoadingSpinner }) {
  const [isSearching, setIsSearching] = useState(false);
  const [searchItems, setSearchItems] = useState([]);
  const [keySkillList, setKeySkillList] = useState([]);
  const [positionList, setPositionList] = useState([]);
  const [projects, setProjects] = useState([]);

  const [user, setUser] = useState({});
  const [usernames, setUserNames] = useState([]);
  const [project, setProject] = useState({});
  const [position, setPosition] = useState({});
  const [englishLevel, setEnglishLevel] = useState({});
  const [selectedKeySkills, setKeySkills] = useState([]);
  const [selectedTechSkills, setTechSkills] = useState([]);
  const [valueIsFired, setIsFired] = useState({});
  const [contractTypes, setContractTypes] = useState([]);
  const [contractType, setContractType] = useState({});

  const searchTimeoutRef = useRef(null);

  const [allUsersCache, setAllUsersCache] = useState([]);
  const [isCacheLoaded, setIsCacheLoaded] = useState(false);

  useEffect(() => {
    const loadProjects = async () => {
      try {
        const response = await fetch(`${config.http.BASE_URL}/Autocomplete-project-fields`);
        let projectsResponse = await response.json();
        projectsResponse = projectsResponse.sort((a, b) =>
          get(a, 'title').localeCompare(get(b, 'title')),
        );
        setProjects(projectsResponse.map((item) => item.title));
      } catch (error) {
        console.error('Ошибка загрузки проектов:', error);
      }
    };
    loadProjects();
  }, []);

  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        const [positionsResp, keySkills, typesData] = await Promise.all([
          getPositions(),
          getKeySkills(),
          getContractTypes(),
        ]);

        const sortedPositions = positionsResp.sort((a, b) =>
          get(a, 'title').localeCompare(get(b, 'title')),
        );
        setPositionList(sortedPositions.map((item) => ({ value: item.title })));

        setKeySkillList(keySkills);

        const types = typesData.map((item) => {
          const title = item?.title
            ?.replace(/_/g, ' ')
            .toLowerCase()
            ?.replace(/\b\w/g, (match) => match.toUpperCase());
          return { searchParam: 'contractType', value: item.title, label: title };
        });
        setContractTypes(types);
      } catch (error) {
        console.error('Ошибка загрузки справочных данных:', error);
      }
    };
    loadReferenceData();
  }, []);

  const filterByParameter = useCallback((data) => {
    const filtered = filter(data, (item) => {
      return item && item.email.includes(companyName);
    });
    return filtered.sort((a, b) => get(a, 'username').localeCompare(get(b, 'username')));
  }, []);

  const performSearch = useCallback(
    async (queryString) => {
      if (isSearching) {
        return;
      }

      setIsSearching(true);
      setLoadingSpinner(true);

      try {
        let response;

        if (!queryString && isCacheLoaded && allUsersCache.length > 0) {
          response = allUsersCache;
        } else {
          response = await getSearchResultsByUsers(queryString);

          if (!queryString && !isCacheLoaded) {
            setAllUsersCache(response || []);
            setIsCacheLoaded(true);
          }
        }

        const cvsRes = filterByParameter(response && response.length ? response : []);
        setUserNames(cvsRes.map((item) => item.username));
        setFilteredUserData(cvsRes.length ? cvsRes : []);
      } catch (error) {
        setFilteredUserData([]);
      } finally {
        setIsSearching(false);
        setLoadingSpinner(false);
      }
    },
    [
      isSearching,
      setLoadingSpinner,
      setFilteredUserData,
      filterByParameter,
      allUsersCache,
      isCacheLoaded,
    ],
  );

  const buildQueryString = useCallback(() => {
    const arr = [].concat(
      user,
      project,
      position,
      englishLevel,
      selectedTechSkills,
      selectedKeySkills,
      contractType,
    );

    let queryString = arr
      .filter((item) => item.value && item.value.length > 0)
      .map((item) => {
        const { searchParam, value } = item;
        const encodedValue = Array.isArray(value)
          ? value.map((v) => encodeURIComponent(v)).join(',')
          : encodeURIComponent(value);
        return `${searchParam}_contains=${encodedValue}`;
      })
      .join('&');

    const separator = queryString === '' ? '' : '&';

    if (valueIsFired === 'Fired') {
      queryString += `${separator}isFired=true`;
    } else if (valueIsFired === 'Not Fired') {
      queryString += `${separator}isFired=false`;
    }

    return queryString;
  }, [
    user,
    project,
    position,
    englishLevel,
    selectedTechSkills,
    selectedKeySkills,
    valueIsFired,
    contractType,
  ]);

  const handleSearch = useCallback(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      const queryString = buildQueryString();
      performSearch(queryString);
    }, 300);
  }, [buildQueryString, performSearch]);

  useEffect(() => {
    if (searchItems.length !== null) {
      if (!searchItems.includes(searchParamList[0].value)) setUser({});
      if (!searchItems.includes(searchParamList[1].value)) setPosition({});
      if (!searchItems.includes(searchParamList[2].value)) setTechSkills([]);
      if (!searchItems.includes(searchParamList[3].value)) setProject({});
      if (!searchItems.includes(searchParamList[4].value)) setKeySkills([]);
      if (!searchItems.includes(searchParamList[5].value)) setEnglishLevel({});
      if (!searchItems.includes(searchParamList[6].value)) setIsFired({});
      if (!searchItems.includes(searchParamList[7].value)) setContractType({});
    }
  }, [searchItems]);

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line
  }, [
    user,
    project,
    position,
    englishLevel,
    selectedKeySkills,
    selectedTechSkills,
    valueIsFired,
    contractType,
  ]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Space size="small" direction="vertical" style={{ width: '100%' }}>
      <OptionFilter
        placeholder="Select Option"
        options={searchParamList}
        setSearchItems={setSearchItems}
      />
      <UserNameFilter
        searchItems={searchItems}
        usernames={usernames}
        setUser={setUser}
        setUserNames={setUserNames}
      />
      <PositionFilter
        searchItems={searchItems}
        positionList={positionList}
        setPosition={setPosition}
      />
      <TechSkillsFilter searchItems={searchItems} setTechSkills={setTechSkills} />
      <ProjectFilter projects={projects} searchItems={searchItems} setProject={setProject} />
      <KeySkillsFilter
        searchItems={searchItems}
        keySkillList={keySkillList}
        setKeySkills={setKeySkills}
        setSearchItems={setSearchItems}
      />
      <EnglishFilter searchItems={searchItems} setEnglishLevel={setEnglishLevel} />
      <IsFireFilter searchItems={searchItems} setIsFired={setIsFired} />
      <ContractTypeFilter
        searchItems={searchItems}
        contractTypes={contractTypes}
        setContractType={setContractType}
      />
    </Space>
  );
}
